import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Badge, Button, Form, InputGroup, Spinner, Dropdown, Modal } from 'react-bootstrap';
import { fetchCourses } from '../../store/slices/marketplaceSlice';
import { fetchTeachers } from '../../store/slices/teachersSlice';
import { getCourseCategoriesApi } from '../../api/marketplace';
import { createCourse } from '../../api/courses';
import { fetchProfile } from '../../store/slices/profileSlice';
import heroImg from '../../assets/images/courses_hero_graphic.png';
import playStoreImg from '../../assets/images/play-store.png';
import appStoreImg from '../../assets/images/app-store.png';
import './courses.scss';

// Safety helpers to extract string values from nested objects (e.g. backend category/instructor shape variations)
const getSafeCategoryName = (cat) => {
  if (!cat) return 'Education';
  if (Array.isArray(cat)) {
    if (cat.length === 0) return 'Education';
    return getSafeCategoryName(cat[0]);
  }
  if (typeof cat === 'object') {
    if (cat.name) {
      return typeof cat.name === 'object' ? getSafeCategoryName(cat.name) : String(cat.name);
    }
    if (cat.title) {
      return typeof cat.title === 'object' ? getSafeCategoryName(cat.title) : String(cat.title);
    }
    return 'Education';
  }
  return String(cat);
};

const getSafeInstructorName = (inst) => {
  if (!inst) return 'Yoospeak Instructor';
  if (typeof inst === 'object') {
    const nameVal = inst.full_name || inst.name;
    if (nameVal) {
      return typeof nameVal === 'object' ? getSafeInstructorName(nameVal) : String(nameVal);
    }
    return 'Yoospeak Instructor';
  }
  return String(inst);
};

export const CoursesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const { courses, pagination, loading: marketplaceLoading } = useSelector((s) => s.marketplace);
  const { teachers, loading: teachersLoading } = useSelector((s) => s.teachers);
  const user = useSelector((s) => s.auth?.user || JSON.parse(localStorage.getItem('user') || '{}'));
  const isAuthenticated = useSelector((s) => s.auth?.isAuthenticated);
  const { profile } = useSelector((s) => s.profile);
  const isTeacher = user?.role === 'teacher' || user?.role === 'Teacher' || user?.user_type === 'teacher' || user?.user_type === 'Teacher';

  // Filter & Search states
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPrice, setSelectedPrice] = useState('All'); // 'All', 'Free', '1-10', '11-20', '21-50', '50+'
  const [selectedLevels, setSelectedLevels] = useState([]); // array of 'Beginner', 'Intermediate', 'Advanced', 'All Levels'
  const [selectedDurations, setSelectedDurations] = useState([]); // array of 'Under 1 Hour', '1 - 5 Hours', '5 - 10 Hours', '10+ Hours'
  const [sortBy, setSortBy] = useState('Most Popular');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  // Add course form states
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newPrice, setNewPrice] = useState(0);
  const [newCoverFile, setNewCoverFile] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const [submittingCourse, setSubmittingCourse] = useState(false);

  // Categories list state
  const [categories, setCategories] = useState([
    { _id: '', name: 'All Courses', icon: '📚' }
  ]);

  // Fetch categories, courses, and teachers on mount
  useEffect(() => {
    if (isAuthenticated && !profile) {
      dispatch(fetchProfile());
    }
  }, [isAuthenticated, profile, dispatch]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCourseCategoriesApi();
        if (data && data.data) {
          const apiCategories = data.data.map((cat) => {
            const nameVal = getSafeCategoryName(cat);
            const match = String(nameVal).match(/(.+?)([\u{1F300}-\u{1FAFF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}])?$/u);
            const name = match ? match[1].trim() : nameVal;
            const details = getCategoryDetails(name);
            return {
              _id: cat._id,
              name: name,
              icon: match && match[2] ? match[2] : details.icon,
            };
          });
          setCategories([
            { _id: '', name: 'All Courses', icon: '📚' },
            ...apiCategories
          ]);
        }
      } catch (err) {
        console.error('Failed to load course categories:', err);
      }
    };

    // Build query params based on current filters and pagination
    const queryParams = {
      category: selectedCategory,
      page: currentPage,
      limit: itemsPerPage
    };
    // Clean up empty params
    Object.keys(queryParams).forEach((key) => {
      if (!queryParams[key]) delete queryParams[key];
    });

    fetchCategories();
    dispatch(fetchCourses(queryParams));
    dispatch(fetchTeachers());
  }, [dispatch, selectedCategory, currentPage, itemsPerPage]);

  // Helper to map category names to custom icons and mockup counts
  const getCategoryDetails = (name) => {
    const norm = name.toLowerCase();
    if (norm.includes('education')) return { icon: '🎓' };
    if (norm.includes('language') || norm.includes('english') || norm.includes('spanish') || norm.includes('speak')) return { icon: '🗣️' };
    if (norm.includes('business')) return { icon: '💼' };
    if (norm.includes('health') || norm.includes('fit') || norm.includes('yoga')) return { icon: '❤️' };
    if (norm.includes('tech') || norm.includes('dev') || norm.includes('code') || norm.includes('software')) return { icon: '💻' };
    if (norm.includes('personal') || norm.includes('self') || norm.includes('lead')) return { icon: '👤' };
    if (norm.includes('life') || norm.includes('travel') || norm.includes('cook')) return { icon: '🌸' };
    if (norm.includes('art') || norm.includes('music') || norm.includes('design') || norm.includes('paint')) return { icon: '🎨' };
    return { icon: '📚' };
  };

  // Duration parser helper
  const getDurationHours = (durationStr) => {
    if (!durationStr) return 4; // fallback
    if (typeof durationStr === 'number') return durationStr;
    const matchHours = durationStr.match(/(\d+)\s*h/i);
    const matchMins = durationStr.match(/(\d+)\s*m/i);
    let hrs = 0;
    if (matchHours) hrs += parseInt(matchHours[1], 10);
    if (matchMins) hrs += parseInt(matchMins[1], 10) / 60;
    return hrs || parseFloat(durationStr) || 4;
  };

  // Normalization mapping for course objects
  const mapCourse = (c) => {
    const title = c.title || c.name || 'Untitled Course';
    const image = c.coverImage || c.cover_image || c.image || null;
    const priceVal = c.price !== undefined && c.price !== null ? Number(c.price) : (c.cost !== undefined && c.cost !== null ? Number(c.cost) : (c.class_fees ? Number(c.class_fees) : 0));
    const priceText = priceVal > 0 ? `$${priceVal}` : 'Free';

    let rawRating = c.average_rating !== undefined && c.average_rating !== null ? c.average_rating : (c.rating !== undefined && c.rating !== null ? c.rating : (c.avg_rating !== undefined && c.avg_rating !== null ? c.avg_rating : 4.0));
    let rating = parseFloat(rawRating);
    if (isNaN(rating)) rating = 4.0;

    let reviewsCount = 0;
    if (c.reviews_count !== undefined && c.reviews_count !== null) {
      reviewsCount = Number(c.reviews_count) || 0;
    } else if (c.teacher_reviews?.total_reviews !== undefined && c.teacher_reviews?.total_reviews !== null) {
      reviewsCount = Number(c.teacher_reviews.total_reviews) || 0;
    } else {
      reviewsCount = Math.floor(Math.random() * 20);
    }

    let students = 0;
    if (Array.isArray(c.students)) {
      students = c.students.length;
    } else if (c.students !== undefined && c.students !== null) {
      students = Number(c.students) || 0;
    } else if (c.enrolled_count !== undefined && c.enrolled_count !== null) {
      students = Number(c.enrolled_count) || 0;
    } else if (c.members_count !== undefined && c.members_count !== null) {
      students = Number(c.members_count) || 0;
    } else {
      students = Math.floor(Math.random() * 80) + 5;
    }

    const duration = c.duration || c.hours || '4h 45m';

    // Badges determination
    let badge = '';
    if (c.badge) {
      badge = c.badge;
    } else if (rating >= 4.8 && students >= 50) {
      badge = 'BESTSELLER';
    } else if (students >= 30) {
      badge = 'POPULAR';
    } else if (c.is_new || rating >= 4.5) {
      badge = 'NEW';
    }

    const rawCategoryName = getSafeCategoryName(c.category);
    const catMatch = String(rawCategoryName).match(/(.+?)([\u{1F300}-\u{1FAFF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}])?$/u);
    const categoryName = catMatch ? catMatch[1].trim() : rawCategoryName;
    const instructor = getSafeInstructorName(c.instructor || c.teacher);

    return {
      id: c._id || c.id,
      title,
      image,
      coverVideo: c.coverVideo || c.cover_video || null,
      priceVal,
      priceText,
      rating,
      reviewsCount,
      students,
      duration,
      badge,
      categoryName,
      instructor,
      raw: c
    };
  };

  // Filter list of courses based on current state parameters
  const getFilteredCourses = () => {
    if (!courses) return [];

    return courses
      .map(mapCourse)
      .filter((c) => {
        // Search Filter
        const matchesSearch = !search ||
          c.title.toLowerCase().includes(search.toLowerCase()) ||
          c.instructor.toLowerCase().includes(search.toLowerCase());

        // Category Filter
        let categoryId = c.raw.category;
        if (typeof categoryId === 'object' && categoryId !== null) {
          categoryId = categoryId._id || categoryId.id;
        }
        const matchesCategory = !selectedCategory ||
          categoryId === selectedCategory ||
          c.raw.category === selectedCategory ||
          c.categoryName === categories.find(cat => cat._id === selectedCategory)?.name ||
          c.categoryName === selectedCategory;

        // Price Filter
        let matchesPrice = true;
        if (selectedPrice === 'Free') {
          matchesPrice = c.priceVal === 0;
        } else if (selectedPrice === '1-10') {
          matchesPrice = c.priceVal > 0 && c.priceVal <= 10;
        } else if (selectedPrice === '11-20') {
          matchesPrice = c.priceVal > 10 && c.priceVal <= 20;
        } else if (selectedPrice === '21-50') {
          matchesPrice = c.priceVal > 20 && c.priceVal <= 50;
        } else if (selectedPrice === '50+') {
          matchesPrice = c.priceVal > 50;
        }

        // Level Filter
        let matchesLevel = true;
        if (selectedLevels.length > 0 && !selectedLevels.includes('All Levels')) {
          const courseLvl = (c.raw.level || 'Beginner').toLowerCase();
          matchesLevel = selectedLevels.some(lvl => courseLvl.includes(lvl.toLowerCase()));
        }

        // Duration Filter
        let matchesDuration = true;
        if (selectedDurations.length > 0) {
          const hrs = getDurationHours(c.duration);
          matchesDuration = selectedDurations.some(dur => {
            if (dur === 'Under 1 Hour') return hrs < 1;
            if (dur === '1 - 5 Hours') return hrs >= 1 && hrs <= 5;
            if (dur === '5 - 10 Hours') return hrs > 5 && hrs <= 10;
            if (dur === '10+ Hours') return hrs > 10;
            return true;
          });
        }

        return matchesSearch && matchesCategory && matchesPrice && matchesLevel && matchesDuration;
      })
      .sort((a, b) => {
        // Sorting Logic
        if (sortBy === 'Most Popular') {
          return b.students - a.students;
        }
        if (sortBy === 'Highest Rated') {
          return b.rating - a.rating;
        }
        if (sortBy === 'Newest') {
          return 1; // Default order
        }
        if (sortBy === 'Price: Low to High') {
          return a.priceVal - b.priceVal;
        }
        if (sortBy === 'Price: High to Low') {
          return b.priceVal - a.priceVal;
        }
        return 0;
      });
  };

  const filteredCourses = getFilteredCourses();
  const totalPages = pagination?.totalPages || Math.max(1, Math.ceil(filteredCourses.length / itemsPerPage));
  const currentCourses = filteredCourses;

  // Toggle helpers for filter arrays
  const handleLevelToggle = (lvl) => {
    setSelectedLevels(prev =>
      prev.includes(lvl) ? prev.filter(item => item !== lvl) : [...prev, lvl]
    );
    setCurrentPage(1);
  };

  const handleDurationToggle = (dur) => {
    setSelectedDurations(prev =>
      prev.includes(dur) ? prev.filter(item => item !== dur) : [...prev, dur]
    );
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSearch('');
    setSelectedCategory('');
    setSelectedPrice('All');
    setSelectedLevels([]);
    setSelectedDurations([]);
    setSortBy('Most Popular');
    setCurrentPage(1);
  };

  const handleCourseClick = (courseId) => {
    navigate(`/course-detail/${courseId}`);
  };

  return (
    <div className="courses-page">
      {/* Hero Section */}
      <div className="courses-hero border-bottom">
        <Container fluid="xl" className="py-4">
          <Row className="align-items-center gx-3 gy-4">
            <Col lg={4} xl={3} md={12}>
              <div className="fs-4 fw-bold text-dark font-poppins mb-1 lh-sm">
                Upgrade Your Skills
              </div>
              <div className="fs-4 fw-bold text-purple font-poppins mb-2 lh-sm">
                Explore Top Courses
              </div>
              <p className="text-muted text-sm mb-0">
                Learn anytime, anywhere with expert-designed courses.
              </p>
            </Col>

            <Col lg={5} xl={6} md={12}>
              <InputGroup className="courses-hero-search rounded-3 shadow-sm bg-white border px-2">
                <Form.Control
                  placeholder="Search courses, topics or instructors..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  className="border-0 shadow-none"
                />
                <Button
                  variant="link"
                  className="text-secondary text-decoration-none px-2 flex-shrink-0"
                  aria-label="Search courses"
                >
                  <i className="bi bi-search fs-5"></i>
                </Button>
              </InputGroup>
            </Col>

            <Col lg={3} xl={3} className="d-none d-lg-block text-center">
              <img
                src={heroImg}
                alt="Education Illustration"
                className="img-fluid courses-hero-illustration"
                style={{ maxHeight: '130px' }}
              />
            </Col>
          </Row>

          {/* Quick Category Pills Scroll */}
          <div className="hero-category-pills-row mt-3">
            <div className="categories-pills-scroll">
              {categories.map((cat, idx) => (
                <button
                  key={cat._id || idx}
                  onClick={() => { setSelectedCategory(cat._id || ''); setCurrentPage(1); }}
                  className={`hero-cat-pill-btn d-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-medium ${(selectedCategory === cat._id || (!selectedCategory && !cat._id))
                    ? 'active'
                    : ''
                    }`}
                >
                  <span>{cat.icon}</span>
                  <span>{cat.name}</span>
                </button>
              ))}
            </div>
          </div>
        </Container>
      </div>

      {/* Main 3-Column Content Layout */}
      <Container fluid="xl" className="main-content-container py-4">
        <Row className="gx-3 gx-xl-4 gy-4">

          {/* LEFT SIDEBAR: FILTERS */}
          <Col lg={3} md={4} sm={12} className="left-sidebar-col">
            <div className="filter-sidebar d-flex flex-column gap-3">

              {/* Filter Section: Categories */}
              <div className="filter-section bg-white border p-3 rounded-3 shadow-sm">
                <h5 className="filter-section-title mb-3 text-navy font-poppins">Categories</h5>
                <div className="category-filter-list">
                  {categories.map((cat, idx) => (
                    <button
                      key={cat._id || idx}
                      onClick={() => { setSelectedCategory(cat._id || ''); setCurrentPage(1); }}
                      className={`category-item-btn d-flex justify-content-between align-items-center w-100 border-0 bg-transparent text-start text-muted fw-medium ${(selectedCategory === cat._id || (!selectedCategory && !cat._id)) ? 'active text-primary' : ''
                        }`}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span className="cat-icon">{cat.icon}</span>
                        <span className="cat-name">{cat.name}</span>
                      </div>
                    </button>
                  ))}
                  <button className="view-all-cats-btn border-0 bg-transparent text-purple fw-semibold mt-2 text-center p-0 text-sm">
                    View All Categories
                  </button>
                </div>
              </div>

              {/* Filter Section: Price */}
              <div className="filter-section bg-white border p-3 rounded-3 shadow-sm">
                <h5 className="filter-section-title mb-3 text-navy font-poppins">Price</h5>
                <div className="price-filter-list d-flex flex-column gap-2 text-muted fw-medium text-sm">
                  {[
                    { label: 'All', value: 'All' },
                    { label: 'Free', value: 'Free' },
                    { label: '$1 - $10', value: '1-10' },
                    { label: '$11 - $20', value: '11-20' },
                    { label: '$21 - $50', value: '21-50' },
                    { label: '$50+', value: '50+' }
                  ].map((p, i) => (
                    <Form.Check
                      key={i}
                      type="radio"
                      id={`price-radio-${i}`}
                      name="price-filter"
                      label={p.label}
                      checked={selectedPrice === p.value}
                      onChange={() => { setSelectedPrice(p.value); setCurrentPage(1); }}
                      className="custom-radio"
                    />
                  ))}
                </div>
              </div>

              {/* Filter Section: Level */}
              <div className="filter-section bg-white border p-3 rounded-3 shadow-sm">
                <h5 className="filter-section-title mb-3 text-navy font-poppins">Level</h5>
                <div className="level-filter-list d-flex flex-column gap-2 text-muted fw-medium text-sm">
                  {['Beginner', 'Intermediate', 'Advanced', 'All Levels'].map((lvl, i) => (
                    <Form.Check
                      key={i}
                      type="checkbox"
                      id={`level-chk-${i}`}
                      label={lvl}
                      checked={selectedLevels.includes(lvl)}
                      onChange={() => handleLevelToggle(lvl)}
                      className="custom-checkbox"
                    />
                  ))}
                </div>
              </div>

              {/* Filter Section: Duration */}
              <div className="filter-section bg-white border p-3 rounded-3 shadow-sm">
                <h5 className="filter-section-title mb-3 text-navy font-poppins">Duration</h5>
                <div className="duration-filter-list d-flex flex-column gap-2 text-muted fw-medium text-sm">
                  {['Under 1 Hour', '1 - 5 Hours', '5 - 10 Hours', '10+ Hours'].map((dur, i) => (
                    <Form.Check
                      key={i}
                      type="checkbox"
                      id={`duration-chk-${i}`}
                      label={dur}
                      checked={selectedDurations.includes(dur)}
                      onChange={() => handleDurationToggle(dur)}
                      className="custom-checkbox"
                    />
                  ))}
                </div>
              </div>

              <Button variant="outline-danger" className="w-100 text-sm fw-semibold" onClick={clearAllFilters}>
                Clear All Filters
              </Button>
            </div>
          </Col>

          {/* MIDDLE COLUMN: COURSES GRID */}
          <Col lg={6} md={8} sm={12} className="middle-content-col">

            {/* Header / Utility Bar */}
            <div className="middle-header-bar d-flex justify-content-between align-items-center mb-3">
              <div>
                <div className="m-0 fs-6 fw-bold text-navy font-poppins">All Courses</div>
                <div className="text-muted text-sm">{filteredCourses.length.toLocaleString()} courses available</div>
              </div>
              {isTeacher && (
                <Button variant="purple" size="sm" className="fw-bold px-3 text-xs" onClick={() => {
                  if (profile) {
                    if (profile.isSubscriptionEnabled === true && profile.has_active_subscription !== true) {
                      navigate('/profile?tab=subscriptions');
                      return;
                    }
                  }
                  setShowAddModal(true);
                }}>
                  <i className="bi bi-plus-circle me-1" /> Add Course
                </Button>
              )}
              <div className="d-flex align-items-center gap-3">

                {/* Sort dropdown */}
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted text-sm text-nowrap">Sort by:</span>
                  <Dropdown onSelect={(key) => { setSortBy(key); setCurrentPage(1); }}>
                    <Dropdown.Toggle variant="light" className="rounded-3 border text-sm fw-medium shadow-sm bg-white" style={{ minWidth: '160px' }}>
                      {sortBy}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="shadow-lg border-0 rounded-3">
                      {['Most Popular', 'Newest', 'Highest Rated', 'Price: Low to High', 'Price: High to Low'].map((opt, i) => (
                        <Dropdown.Item key={i} eventKey={opt} active={sortBy === opt} className="text-sm py-2 px-3">
                          {opt}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                {/* View Toggles (Mocked) */}
                <div className="d-none d-sm-flex align-items-center gap-2">
                  <Button size="sm" variant="purple" className="active">
                    <i className="bi bi-grid-fill"></i>
                  </Button>
                  <Button size="sm" variant="outline-purple" className="">
                    <i className="bi bi-list"></i>
                  </Button>
                </div>

              </div>
            </div>

            {/* Loading Indicator */}
            {marketplaceLoading.courses && (
              <div className="d-flex justify-content-center py-5">
                <div className="text-center">
                  <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                  <p className="mt-3 text-muted fw-semibold">Loading courses...</p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!marketplaceLoading.courses && filteredCourses.length === 0 && (
              <div className="empty-state text-center py-5 bg-white border border-light-subtle rounded-4 p-5 shadow-sm">
                <i className="bi bi-journal-x text-muted" style={{ fontSize: '4rem', opacity: 0.3 }}></i>
                <h4 className="fw-bold mt-4 mb-2 text-navy">No courses matched</h4>
                <p className="text-muted mb-4 text-sm">We couldn't find any courses matching your specific search or filter criteria.</p>
                <Button variant="primary" className="rounded-pill px-4" onClick={clearAllFilters}>Reset Filters</Button>
              </div>
            )}

            {/* Courses Grid */}
            {!marketplaceLoading.courses && filteredCourses.length > 0 && (
              <Row className="g-3 row-cols-1 row-cols-sm-2 row-cols-xl-3">
                {currentCourses.map((c) => (
                  <Col key={c.id}>
                    <div className="course-item-card h-100 bg-white border rounded-3 shadow-sm overflow-hidden d-flex flex-column pointer transition-all" onClick={() => handleCourseClick(c.id)}>

                      {/* Image/Video container with overlays */}
                      <div className="card-image-wrapper position-relative overflow-hidden" style={{ height: '160px' }}>
                        {c.image && c.image !== 'null' ? (
                          <img src={c.image} alt={c.title} className="w-100 h-100 object-fit-cover card-img-zoom" />
                        ) : c.coverVideo ? (
                          <video
                            src={c.coverVideo}
                            className="w-100 h-100 object-fit-cover card-img-zoom"
                            muted
                            controls
                            onMouseEnter={(e) => {
                              e.target.muted = false;
                              e.target.play().catch(() => { });
                            }}
                            onMouseLeave={(e) => {
                              e.target.muted = true;
                              e.target.pause();
                            }}
                          />
                        ) : (
                          <img src="https://images.unsplash.com/photo-1513258496099-48168024aec0?w=500&h=280&fit=crop" alt={c.title} className="w-100 h-100 object-fit-cover card-img-zoom" />
                        )}

                        {/* Bestseller/Popular/New Badge */}
                        {c.badge && (
                          <div className="position-absolute top-0 start-0 m-3 z-3">
                            <Badge className={`badge-overlay rounded-pill fw-bold text-uppercase ${c.badge === 'BESTSELLER' ? 'bg-success text-white' :
                              c.badge === 'POPULAR' ? 'bg-indigo text-white' : 'bg-warning text-dark'
                              }`}>
                              {c.badge}
                            </Badge>
                          </div>
                        )}

                        {/* Duration Badge */}
                        <div className="position-absolute bottom-0 start-0 m-3 z-3">
                          <span className="duration-tag text-white rounded-pill px-2 py-1 fw-semibold">
                            <i className="bi bi-clock me-1 text-white"></i>
                            {c.duration}
                          </span>
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="card-body p-3 d-flex flex-column justify-content-between flex-grow-1">
                        <div className="d-flex flex-column gap-2">
                          <div className="card-course-title fs-6 fw-bold text-navy text-truncate-2">
                            {c.title}
                          </div>

                          {/* Instructor Name with Verified Icon */}
                          <div className="card-instructor text-xs text-muted d-flex align-items-center">
                            <i className="bi bi-person-fill text-primary-custom me-2"></i>
                            <span className="text-truncate">{c.instructor}</span>
                            <i className="bi bi-patch-check-fill text-blue ms-1" title="Verified Publisher"></i>
                          </div>

                          {/* Ratings and Students */}
                          <div className="card-stats d-flex align-items-center gap-2 text-xs text-muted flex-wrap">
                            <div className="d-flex align-items-center gap-1">
                              <i className="bi bi-star-fill text-warning"></i>
                              <span className="fw-bold text-dark">{c.rating.toFixed(1)}</span>
                              <span className="text-secondary">({c.reviewsCount} Reviews)</span>
                            </div>
                            <div className="d-flex align-items-center gap-1">
                              <i className="bi bi-people-fill text-primary-custom"></i>
                              <span className="fw-semibold text-dark">{c.students.toLocaleString()} Students</span>
                            </div>
                          </div>

                          {/* Card Footer */}
                          <div className="card-footer-custom d-flex flex-wrap justify-content-between align-items-center">
                            <Badge className="category-badge-pill bg-light text-muted border border-light-subtle rounded-pill px-3 py-2 text-xs fw-semibold">
                              {c.categoryName}
                            </Badge>
                            <span className="card-price fw-bold text-base text-primary-custom">{c.priceText}</span>
                          </div>
                        </div>
                      </div>

                    </div>
                  </Col>
                ))}
              </Row>
            )}

            {/* Pagination Row */}
            {!marketplaceLoading.courses && filteredCourses.length > 0 && totalPages > 1 && (
              <div className="pagination-row d-flex justify-content-center align-items-center mt-4 gap-2">
                <Button
                  variant="light"
                  size="sm"
                  onClick={() => {
                    setCurrentPage(prev => Math.max(1, prev - 1));
                    window.scrollTo({ top: 400, behavior: 'smooth' });
                  }}
                  disabled={currentPage === 1}
                  className="pagination-btn border shadow-sm"
                >
                  <i className="bi bi-chevron-left"></i>
                </Button>

                {Array.from({ length: totalPages }).map((_, idx) => {
                  const pageNum = idx + 1;
                  return (
                    <Button
                      key={idx}
                      size="sm"
                      variant={currentPage === pageNum ? 'primary' : 'light'}
                      onClick={() => {
                        setCurrentPage(pageNum);
                        window.scrollTo({ top: 400, behavior: 'smooth' });
                      }}
                      className={`pagination-btn border shadow-sm ${currentPage === pageNum ? 'text-white' : 'bg-white'
                        }`}
                    >
                      {pageNum}
                    </Button>
                  );
                })}

                <Button
                  variant="light"
                  size="sm"
                  onClick={() => {
                    setCurrentPage(prev => Math.min(totalPages, prev + 1));
                    window.scrollTo({ top: 400, behavior: 'smooth' });
                  }}
                  disabled={currentPage === totalPages}
                  className="pagination-btn border shadow-sm"
                >
                  <i className="bi bi-chevron-right"></i>
                </Button>
              </div>
            )}

          </Col>

          {/* RIGHT SIDEBAR */}
          <Col lg={3} className="right-sidebar-col">
            <div className="right-sidebar d-flex flex-column gap-3">

              {/* Section 1: Top Instructors */}
              <div className="sidebar-widget bg-white p-3 rounded-3 shadow-sm border">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="widget-title m-0 text-navy font-poppins">Top Instructors</h5>
                  <Link to="/teachers" className="widget-link fw-bold text-decoration-none">
                    View All
                  </Link>
                </div>

                <div className="instructors-list d-flex flex-column gap-3">
                  {teachersLoading && <div className="text-center py-3"><Spinner animation="border" size="sm" variant="primary" /></div>}

                  {!teachersLoading && teachers.slice(0, 5).map((inst, i) => {
                    const name = inst.full_name || inst.name || 'Instructor';
                    const image = inst.profile_picture || inst.image || 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop';
                    const coursesCount = inst.courses_count || 0;
                    const studentsCount = inst.students || 0;

                    return (
                      <Link key={inst._id || i} to={`/teachers/${inst._id}`} className="instructor-row-item d-flex align-items-center gap-2 text-decoration-none rounded-3 transition-all">
                        <img src={image} alt={name} className="instructor-avatar user-avatar-lg" />
                        <div className="instructor-info min-w-0 overflow-hidden flex-grow-1">
                          <h6 className="instructor-name text-navy fw-bold text-sm  mb-1 d-flex align-items-center">
                            <span className="text-truncate">{name}</span>
                            <i className="bi bi-patch-check-fill text-sm text-blue ms-1"></i>
                          </h6>
                          <div className="instructor-metrics d-flex flex-wrap row-gap-1 column-gap-2 text-muted text-xs">
                            <span>{coursesCount} Courses</span>
                            <span>{studentsCount} Students</span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Section 2: Become an Instructor Promotion */}
              <div className="sidebar-widget become-instructor-widget p-3 rounded-4 shadow-sm border-0 position-relative overflow-hidden bg-indigo-gradient text-white">
                <div className="widget-text-content position-relative z-3">
                  <h4 className="widget-promo-title fw-bold font-poppins mb-2">Become an Instructor</h4>
                  <p className="widget-promo-subtext text-white text-opacity-80 text-xs mb-4" style={{ maxWidth: '170px' }}>
                    Share your knowledge and earn money by teaching online.
                  </p>
                  <Button variant="light" size="sm" className="btn-promo-cta rounded-pill px-4 py-2 text-navy text-xs fw-bold border-0 shadow-sm transition-all" onClick={() => navigate('/login')}>
                    Start Teaching
                  </Button>
                </div>
                {/* SVG Graphics / Illustration Overlay */}
                <div className="widget-illustration-overlay position-absolute bottom-0 end-0 m-2 opacity-30 z-1">
                  <i className="bi bi-mortarboard" style={{ fontSize: '6rem', color: '#fff' }}></i>
                </div>
              </div>

              {/* Section 3: What's New */}
              <div className="sidebar-widget bg-white p-3 rounded-3 shadow-sm border">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="widget-title m-0 text-navy font-poppins">What's New</h5>
                  <span className="widget-link text-sm fw-bold pointer" onClick={() => setSelectedCategory('')}>
                    View All
                  </span>
                </div>

                <div className="new-courses-list d-flex flex-column gap-3">
                  {courses.slice(0, 3).map((item, idx) => {
                    const c = mapCourse(item);
                    return (
                      <div key={idx} className="new-course-row-item d-flex align-items-center gap-2 pointer" onClick={() => handleCourseClick(c.id)}>
                        <img src={c.image || 'https://images.unsplash.com/photo-1513258496099-48168024aec0?w=500&h=280&fit=crop'} alt={c.title} className="new-course-thumbnail rounded-3 object-fit-cover border border-light" style={{ width: '55px', height: '48px' }} />
                        <div className="new-course-info min-w-0 overflow-hidden flex-grow-1">
                          <h6 className="new-course-title text-navy fw-bold text-sm text-truncate-2 mb-1" title={c.title}>
                            {c.title}
                          </h6>
                          <div className="new-course-date text-muted text-xs">
                            {idx === 0 ? 'New Course &bull; 2 days ago' : idx === 1 ? 'New Course &bull; 4 days ago' : 'New Course &bull; 1 week ago'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Section 4: Download App Section */}
              <div className="sidebar-widget app-download-widget bg-white p-3 rounded-3 shadow-sm border">
                <h5 className="widget-title text-navy font-poppins mb-2">Download the App</h5>
                <p className="widget-subtitle text-muted text-xs mb-3">
                  Learn on the go. Anytime, anywhere.
                </p>
                <div className="d-flex flex-wrap gap-2 align-items-stretch">
                  <a href="https://apps.apple.com/in/app/yoospeak/id6746839854" target="_blank" rel="noopener noreferrer" className="d-block transition-all hover-scale-sm">
                    <img src={appStoreImg} alt="Download on App Store" className="img-fluid rounded-3 w-100" style={{ maxHeight: '36px', objectFit: 'contain' }} />
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.yoospeak.learning" target="_blank" rel="noopener noreferrer" className="d-block transition-all hover-scale-sm">
                    <img src={playStoreImg} alt="Get it on Google Play" className="img-fluid rounded-3 w-100" style={{ maxHeight: '36px', objectFit: 'contain' }} />
                  </a>
                </div>
              </div>

            </div>
          </Col>

        </Row>
      </Container>

      {/* ADD COURSE MODAL */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-navy">Create New Course</Modal.Title>
        </Modal.Header>
        <Form onSubmit={async (e) => {
          e.preventDefault();
          try {
            setSubmittingCourse(true);
            const fd = new FormData();
            fd.append('title', newTitle);
            fd.append('description', newDesc);
            fd.append('price', String(newPrice));
            fd.append('category', newCategory);
            if (newCoverFile) {
              fd.append('cover_image', newCoverFile);
              fd.append('coverImage', newCoverFile);
              fd.append('image', newCoverFile);
            }
            await createCourse(fd);
            alert('Course created successfully!');
            setShowAddModal(false);
            setNewTitle('');
            setNewDesc('');
            setNewPrice(0);
            setNewCoverFile(null);
            setNewCategory('');

            // Refresh course list
            dispatch(fetchCourses({
              category: selectedCategory,
              page: currentPage,
              limit: itemsPerPage
            }));
          } catch (err) {
            console.error('Failed to create course', err);
            alert('Failed to create course.');
          } finally {
            setSubmittingCourse(false);
          }
        }}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Course Title</Form.Label>
              <Form.Control
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. English Speaking Masterclass"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Course outline and curriculum..."
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Price ($)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={newPrice}
                onChange={(e) => setNewPrice(Number(e.target.value))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Category</Form.Label>
              <Form.Select
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                required
              >
                <option value="">Select Category</option>
                {categories.filter(c => c._id).map((cat) => (
                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Cover Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => setNewCoverFile(e.target.files[0])}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={submittingCourse}>
              {submittingCourse ? 'Creating...' : 'Create Course'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};
